/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import React, { useEffect, useState } from "react";
import CheckoutCart from "../../components/Payment/CheckoutCart/CheckoutCart";
import LegalbirdLoader from "../../components/ContentLoader/LegalbirdLoader";
import Container from "../../components/Container/Container";
import { useNavigate } from "react-router-dom";
import { AddressElement, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createResource, queryKeys, updateResource } from "../../services/reactQuery/reactQueryService";
import { useCustomer } from "../../provider/Customer/CustomerProvider";
import { useCheckoutOrder } from "../../provider/CheckoutOrder/CheckoutOrderProvider";
import { Stack, Typography } from "@mui/material";
import ButtonLoading from "../../components/Button/ButtonLoading";
import { BASE_URL } from "../../config/_enviroment";
import ContentBox from "../../components/Box/ContentBox/ContentBox";
import { Address } from "@stripe/stripe-js";
import { PaymentTransaction } from "../../types/Entities/PaymentTransaction";

const Checkout = ({ transaction }: { transaction: PaymentTransaction }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [stripeError, setStripeError] = useState<undefined | string>("");
  const { customer } = useCustomer();
  const { checkoutOrder, isLoading: isLoadingCheckoutOrder } = useCheckoutOrder();
  const elements = useElements();
  const stripe = useStripe();
  const navigate = useNavigate();
  const createMutation = useMutation(createResource);
  const queryClient = useQueryClient();
  const updateMutation = useMutation(updateResource, {
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, variables.id), data),
  });

  useEffect(() => {
    if (!!checkoutOrder?.paidDate) {
      navigate("/checkout/bestaetigung?transactionId=" + transaction.id + "&orderId=" + checkoutOrder.orderIdentifier);
    }
  }, [isLoadingCheckoutOrder, checkoutOrder]);

  const transformStripeAddressData = (addressData: Address) => ({
    streetAddress: addressData.line1,
    addressLocality: addressData.city,
    postalCode: addressData.postal_code,
    type: "invoice",
  });

  if (!stripe || !elements) {
    return <LegalbirdLoader />;
  }

  const handleSubmit = async () => {
    setIsLoading(true);

    const addressElement = elements ? elements.getElement("address") : null;
    if (!addressElement) {
      return;
    }

    const addressDataResult = await addressElement.getValue();
    const addressData = transformStripeAddressData(addressDataResult.value.address);

    const invoiceAddress = customer!.person.invoiceAddress;
    if (invoiceAddress) {
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: invoiceAddress.id,
        data: addressData,
      });
    } else {
      const invoiceResponse = await createMutation.mutateAsync({ uri: "/addresses", data: addressData });
      await updateMutation.mutateAsync({
        uri: "/people",
        id: customer!.person.id,
        data: {
          addresses: ["/addresses/" + invoiceResponse.id],
        },
      });
    }

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: BASE_URL + "/checkout/bestaetigung?transactionId=" + transaction.id + "&orderId=" + checkoutOrder.orderIdentifier,
      },
    });

    if (error) {
      setStripeError(error.message);
    }

    setIsLoading(false);
  };

  if (!checkoutOrder) {
    return (
      <Container sx={{ width: 250 }}>
        <LegalbirdLoader />
      </Container>
    );
  }

  return (
    <>
      <CheckoutCart item={checkoutOrder.product.checkoutItem} />
      <Container>
        <ContentBox headline={"Zahlart auswählen"}>
          <Stack spacing={2}>
            <PaymentElement options={{ layout: { type: "accordion" } }} />
            <AddressElement options={{ mode: "billing" }} />
          </Stack>
          {stripeError && (
            <Typography variant="body2" sx={{ color: "red", marginTop: "1rem", textAlign: "center" }}>
              {stripeError}
            </Typography>
          )}
          <ButtonLoading isLoading={isLoading} sx={{ marginTop: "1rem" }} fullWidth variant={"contained"} onClick={handleSubmit}>
            Bezahlen
          </ButtonLoading>
        </ContentBox>
      </Container>
    </>
  );
};
export default Checkout;
